import {useTranslation} from 'react-i18next';
import React, {useEffect, useState} from 'react';
import div from '@mui/material/Paper';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {observationConfig} from '../../../../../store/reducers/externalConfig';
import AgyleDatePicker from '../../../../../components/ui/fields/agyleDatePicker';
import moment from 'moment';
import {hasPermission, Permissions} from '../../../../../store/reducers/user';
import _ from 'lodash';
import * as _debounce from 'lodash/debounce';
import {emitChangedWait} from '../obsUtils';
import {calculateArrivalDate} from '../../../../../util/patientUtil';
import InformationBox from '../../../../../components/other/informationBox';
import {obsStyles} from '../obsStyle';

function ObsRetrospective(props) {
    const classes = obsStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const {patient, observations, onErrorPicker} = props;

    //
    const [startedDate, setStartedDate] = useState(moment());
    const [minDate, setMinDate] = useState(null);
    const [isAmbulanceObs, setIsAmbulanceObs] = useState(false);

    // Config settings
    const obsCfg = useSelector(observationConfig);
    const canUpdate = dispatch(hasPermission(Permissions.AGYLE_CAN_EDIT_FREQUENCY_CANCEL_OBSERVATIONS))
        && obsCfg?.retrospective;

    // Check if its ambulance observation
    useEffect(() => {
        if (startedDate?.isValid()) {
            const arrivalDate = moment(calculateArrivalDate(patient));
            setIsAmbulanceObs(startedDate.isBefore(arrivalDate));
        } else {
            setIsAmbulanceObs(false);
        }
    }, [patient, startedDate]);

    // Calculate min date
    useEffect(() => {
        if (!_.isEmpty(observations)) {
            const lastObs = _.chain(observations)
                .filter(ob => !ob.canceledDate)
                .orderBy(['startedDate'], ['desc'])
                .first()
                .value();
            if (lastObs) setMinDate(lastObs?.startedDate || null);
            else {
                const arrivalDate = moment(calculateArrivalDate(patient));
                setMinDate(arrivalDate.subtract(1, 'week'));
            }
        } else {
            const arrivalDate = moment(calculateArrivalDate(patient));
            setMinDate(arrivalDate.subtract(1, 'week'));
        }
    }, [observations, patient]);

    // Update new start date
    useEffect(() => {
        if (canUpdate && startedDate?.isValid()) emitChanged();
    }, [startedDate, canUpdate, isAmbulanceObs]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            lazyEmitOnChange(
                {
                    startedDate,
                    ambulanceObs: isAmbulanceObs,
                }
                , props.onChange,
            );
        }
    };

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <AgyleDatePicker
                    type="dateTime"
                    value={startedDate}
                    onChange={setStartedDate}
                    label={t('Assessed')}
                    disableFuture
                    disablePast={false}
                    minDate={minDate}
                    maxDate={moment()}
                    disabled={!canUpdate}
                    onError={onErrorPicker}
                />
            </div>

            {isAmbulanceObs &&
                <InformationBox type="info">
                    {t('Observations prior to attendance')}
                </InformationBox>
            }
        </div>
    );
}

ObsRetrospective.propTypes = {
    onChange: PropTypes.func,
    patient: PropTypes.object,
    observations: PropTypes.array,
    onErrorPicker: PropTypes.func,
};

ObsRetrospective.defaultProps = {
    observations: [],
};

export default ObsRetrospective;